import {
  AfterViewInit,
  Component,
  ElementRef,
  forwardRef,
  Input,
  ViewChild,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

/**
 * Lightweight inline rich-text editor for short content (e.g. a program cue).
 *
 * Renders a `contenteditable` element that behaves as a drop-in replacement for
 * a text input via `ControlValueAccessor`, so `[(ngModel)]="item.text"` keeps
 * working. While focused it shows a tiny floating toolbar (Bold, Italic,
 * Underline, Link). Output is restricted to a small allowlist of inline tags so
 * the stored HTML stays clean and safe for the (unescaped) public renderer.
 */
@Component({
  selector: 'app-inline-rich-edit',
  template: `
    <div
      #editor
      class="ire-editable"
      [class.is-empty]="isEmpty"
      [attr.data-placeholder]="placeholder"
      [attr.contenteditable]="disabled ? 'false' : 'true'"
      (input)="onInput()"
      (focus)="onFocus()"
      (blur)="onBlur()"
      (paste)="onPaste($event)"
      (keydown)="onKeydown($event)"
    ></div>

    <div
      class="ire-toolbar"
      *ngIf="toolbarVisible"
      (mousedown)="$event.preventDefault()"
    >
      <button type="button" title="Bold" (mousedown)="exec('bold')"><b>B</b></button>
      <button type="button" title="Italic" (mousedown)="exec('italic')"><i>I</i></button>
      <button type="button" title="Underline" (mousedown)="exec('underline')"><u>U</u></button>
      <button type="button" title="Link" (mousedown)="createLink()">Link</button>
    </div>
  `,
  styles: [
    `
      :host {
        position: relative;
        display: block;
      }
      .ire-editable {
        min-height: 1.25rem;
        outline: none;
        white-space: pre-wrap;
        word-break: break-word;
        cursor: text;
      }
      .ire-editable.is-empty::before {
        content: attr(data-placeholder);
        color: #9ca3af;
        pointer-events: none;
      }
      .ire-editable a {
        color: #2563eb;
        text-decoration: underline;
      }
      .ire-toolbar {
        position: absolute;
        top: -2.25rem;
        left: 0;
        z-index: 1000;
        display: flex;
        gap: 2px;
        padding: 3px;
        background: #1f2937;
        border-radius: 5px;
        box-shadow: 0 2px 6px rgba(0, 0, 0, 0.25);
      }
      .ire-toolbar button {
        min-width: 26px;
        height: 24px;
        padding: 0 6px;
        background: transparent;
        border: none;
        border-radius: 3px;
        color: #fff;
        font-size: 0.8125rem;
        line-height: 1;
        cursor: pointer;
      }
      .ire-toolbar button:hover {
        background: #374151;
      }
    `,
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => InlineRichEditComponent),
      multi: true,
    },
  ],
})
export class InlineRichEditComponent implements ControlValueAccessor, AfterViewInit {
  @Input() placeholder = '';

  @ViewChild('editor') editorRef?: ElementRef<HTMLDivElement>;

  toolbarVisible = false;
  isEmpty = true;
  disabled = false;

  private pending = '';
  private viewReady = false;
  private blurTimer: ReturnType<typeof setTimeout> | null = null;

  private static readonly ALLOWED = new Set(['B', 'STRONG', 'I', 'EM', 'U', 'A', 'BR']);

  private onChange: (value: string) => void = () => {};
  private onTouched: () => void = () => {};

  ngAfterViewInit(): void {
    this.viewReady = true;
    this.render();
  }

  // --- ControlValueAccessor -------------------------------------------------

  writeValue(value: string): void {
    this.pending = value || '';
    if (this.viewReady) {
      this.render();
    }
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  // --- DOM events -----------------------------------------------------------

  onInput(): void {
    this.emit();
    this.updateEmpty();
  }

  onFocus(): void {
    if (this.blurTimer) {
      clearTimeout(this.blurTimer);
      this.blurTimer = null;
    }
    this.toolbarVisible = true;
  }

  onBlur(): void {
    this.onTouched();
    // Delay hiding so a toolbar button's mousedown can run before blur removes it.
    this.blurTimer = setTimeout(() => {
      this.toolbarVisible = false;
    }, 200);
  }

  onPaste(event: ClipboardEvent): void {
    event.preventDefault();
    const text = event.clipboardData?.getData('text/plain') ?? '';
    document.execCommand('insertText', false, text);
  }

  onKeydown(event: KeyboardEvent): void {
    // Keep line breaks as <br> rather than letting the browser insert <div>/<p>.
    if (event.key === 'Enter') {
      event.preventDefault();
      document.execCommand('insertLineBreak');
    }
  }

  // --- Toolbar actions ------------------------------------------------------

  exec(command: 'bold' | 'italic' | 'underline'): void {
    document.execCommand('styleWithCSS', false, 'false');
    document.execCommand(command, false);
    this.editorRef?.nativeElement.focus();
    this.emit();
  }

  createLink(): void {
    const existing = this.selectedAnchor();
    const current = existing?.getAttribute('href') || '';
    const input = window.prompt('Enter URL (leave empty to remove link):', current);
    if (input === null) {
      return;
    }
    const url = input.trim();
    if (url === '') {
      document.execCommand('unlink', false);
    } else {
      const href = this.ensureProtocol(url);
      if (href) {
        document.execCommand('createLink', false, href);
      }
    }
    this.editorRef?.nativeElement.focus();
    this.emit();
  }

  // --- Internals ------------------------------------------------------------

  private render(): void {
    const el = this.editorRef?.nativeElement;
    if (!el) {
      return;
    }
    el.innerHTML = this.normalize(this.pending);
    this.updateEmpty();
  }

  private emit(): void {
    const el = this.editorRef?.nativeElement;
    if (!el) {
      return;
    }
    this.onChange(this.normalize(el.innerHTML));
  }

  private updateEmpty(): void {
    const el = this.editorRef?.nativeElement;
    if (!el) {
      return;
    }
    const hasText = (el.textContent || '').trim().length > 0;
    const hasBreak = !!el.querySelector('br');
    this.isEmpty = !hasText && !hasBreak;
  }

  private selectedAnchor(): HTMLAnchorElement | null {
    const editor = this.editorRef?.nativeElement;
    const selection = window.getSelection();
    if (!editor || !selection || selection.rangeCount === 0) {
      return null;
    }
    let node: Node | null = selection.anchorNode;
    while (node && node !== editor) {
      if (node.nodeType === Node.ELEMENT_NODE && (node as HTMLElement).tagName === 'A') {
        return node as HTMLAnchorElement;
      }
      node = node.parentNode;
    }
    return null;
  }

  /** Strip everything except a small allowlist of inline formatting tags. */
  private normalize(html: string): string {
    const container = document.createElement('div');
    container.innerHTML = html || '';
    this.sanitize(container);
    // Turn literal newlines into real <br> tags. HTML collapses bare \n to a
    // space, so without this, line breaks (legacy content, pasted text, or the
    // editor's pre-wrap display) would silently disappear on the public page.
    this.newlinesToBr(container);
    return container.innerHTML;
  }

  /**
   * Replace every "\n" inside text nodes with a <br> element. Operates on text
   * nodes (not the serialized string) so insignificant formatting whitespace
   * between tags is left alone. \r\n / \r are normalized to \n first.
   */
  private newlinesToBr(parent: Node): void {
    const textNodes: Text[] = [];
    const walker = document.createTreeWalker(parent, NodeFilter.SHOW_TEXT);
    let node = walker.nextNode();
    while (node) {
      textNodes.push(node as Text);
      node = walker.nextNode();
    }
    for (const textNode of textNodes) {
      const data = textNode.data.replace(/\r\n?/g, '\n');
      if (data.indexOf('\n') === -1) {
        continue;
      }
      const parts = data.split('\n');
      const frag = document.createDocumentFragment();
      parts.forEach((part, i) => {
        if (i > 0) {
          frag.appendChild(document.createElement('br'));
        }
        if (part) {
          frag.appendChild(document.createTextNode(part));
        }
      });
      textNode.parentNode?.replaceChild(frag, textNode);
    }
  }

  private sanitize(parent: Node): void {
    const children = Array.from(parent.childNodes);
    for (const child of children) {
      if (child.nodeType === Node.COMMENT_NODE) {
        child.parentNode?.removeChild(child);
        continue;
      }
      if (child.nodeType !== Node.ELEMENT_NODE) {
        continue;
      }

      const el = child as HTMLElement;
      const tag = el.tagName;

      if (!InlineRichEditComponent.ALLOWED.has(tag)) {
        // Unwrap: sanitize its contents, then splice them in place of the element.
        this.sanitize(el);
        const owner = el.parentNode;
        if (owner) {
          const isBlock = tag === 'DIV' || tag === 'P' || tag === 'LI';
          if (isBlock && el.previousSibling) {
            owner.insertBefore(document.createElement('br'), el);
          }
          while (el.firstChild) {
            owner.insertBefore(el.firstChild, el);
          }
          owner.removeChild(el);
        }
        continue;
      }

      if (tag === 'A') {
        const href = this.ensureProtocol(el.getAttribute('href') || '');
        this.stripAttributes(el);
        if (href) {
          el.setAttribute('href', href);
          el.setAttribute('target', '_blank');
          el.setAttribute('rel', 'noopener noreferrer');
        }
      } else {
        this.stripAttributes(el);
      }

      this.sanitize(el);
    }
  }

  private stripAttributes(el: HTMLElement): void {
    for (const attr of Array.from(el.attributes)) {
      el.removeAttribute(attr.name);
    }
  }

  /** Allow only http(s)/mailto/tel and relative/anchor URLs; block the rest. */
  private ensureProtocol(url: string): string {
    const value = (url || '').trim();
    if (!value) {
      return '';
    }
    if (/^(https?:|mailto:|tel:)/i.test(value)) {
      return value;
    }
    if (value.startsWith('/') || value.startsWith('#')) {
      return value;
    }
    // Any other explicit scheme (javascript:, data:, etc.) is rejected.
    if (/^[a-z][a-z0-9+.-]*:/i.test(value)) {
      return '';
    }
    return 'https://' + value;
  }
}
